import { Injectable, Signal, signal } from '@angular/core';
import { IDomainSettings } from './domain-settings.interface';
import { TAppDomain } from './domain-settings.types';

export const DEFAULT_DOMAIN_SETTINGS: IDomainSettings = {
  isStickyFooter: false,
  isHiddenProgress: false,
  confirmBeforeExit: true // this setting defines whether the popup shoudl be opened
};

@Injectable({ providedIn: 'root' })
export class DomainSettings {
  private static readonly _settingsMap = new Map<TAppDomain, IDomainSettings>()
    .set('entry', DomainSettings.createSettings())
    .set('identity', DomainSettings.createSettings())
    .set('owner', DomainSettings.createSettings())
    .set('branch', DomainSettings.createSettings())
    .set('welcome', DomainSettings.createSettings())
    .set('id-card', DomainSettings.createSettings())
    .set('account', DomainSettings.createSettings())
    .set('status', DomainSettings.createSettings())
    .set('business-brand', DomainSettings.createSettings())
    .set('address', DomainSettings.createSettings())
    .set('clientele', DomainSettings.createSettings())
    .set('physical-store', DomainSettings.createSettings())
    .set('overseas', DomainSettings.createSettings())
    .set('defrayment', DomainSettings.createSettings())
    .set('funds', DomainSettings.createSettings())
    .set('persona', DomainSettings.createSettings())
    .set('beneficiaries', DomainSettings.createSettings())
    .set('refused', DomainSettings.createSettings())
    .set('signature', DomainSettings.createSettings())
    .set('channels', DomainSettings.createSettings())
    .set('customer-services/activity', DomainSettings.createSettings())
    .set('customer-services/billing', DomainSettings.createSettings())
    .set('summary', DomainSettings.createSettings({ isHiddenProgress: true }))
    .set('lead/summary', DomainSettings.createSettings({ isHiddenProgress: true, confirmBeforeExit: false }))
    .set('terminals', DomainSettings.createSettings())
    .set('web-site/url', DomainSettings.createSettings())
    .set('web-site/products', DomainSettings.createSettings())
    .set('message', DomainSettings.createSettings({ isHiddenProgress: true }))
    .set('upsell', DomainSettings.createSettings())
    .set('message/guide/persona-guide', DomainSettings.createSettings({ isHiddenProgress: true }))
    .set('message/confirm', DomainSettings.createSettings({ confirmBeforeExit: false, isHiddenProgress: true }))
    .set('clients-privacy', DomainSettings.createSettings())
    .set('disclosure', DomainSettings.createSettings());

  /**
   * public interface
   **/
  static entry: Signal<IDomainSettings> = signal(DomainSettings.getSettings('entry'));
  static identity: Signal<IDomainSettings> = signal(DomainSettings.getSettings('identity'));
  static owner: Signal<IDomainSettings> = signal(DomainSettings.getSettings('owner'));
  static branch: Signal<IDomainSettings> = signal(DomainSettings.getSettings('branch'));
  static welcome: Signal<IDomainSettings> = signal(DomainSettings.getSettings('welcome'));
  static idCard: Signal<IDomainSettings> = signal(DomainSettings.getSettings('id-card'));
  static account: Signal<IDomainSettings> = signal(DomainSettings.getSettings('account'));
  static status: Signal<IDomainSettings> = signal(DomainSettings.getSettings('status'));
  static businessBrand: Signal<IDomainSettings> = signal(DomainSettings.getSettings('business-brand'));
  static address: Signal<IDomainSettings> = signal(DomainSettings.getSettings('address'));
  static clientele: Signal<IDomainSettings> = signal(DomainSettings.getSettings('clientele'));
  static physicalStore: Signal<IDomainSettings> = signal(DomainSettings.getSettings('physical-store'));
  static overseas: Signal<IDomainSettings> = signal(DomainSettings.getSettings('overseas'));
  static defrayment: Signal<IDomainSettings> = signal(DomainSettings.getSettings('defrayment'));
  static funds: Signal<IDomainSettings> = signal(DomainSettings.getSettings('funds'));
  static persona: Signal<IDomainSettings> = signal(DomainSettings.getSettings('persona'));
  static beneficiaries: Signal<IDomainSettings> = signal(DomainSettings.getSettings('beneficiaries'));
  static refused: Signal<IDomainSettings> = signal(DomainSettings.getSettings('refused'));
  static signature: Signal<IDomainSettings> = signal(DomainSettings.getSettings('signature'));
  static channels: Signal<IDomainSettings> = signal(DomainSettings.getSettings('channels'));
  static customerServicesActivity: Signal<IDomainSettings> = signal(DomainSettings.getSettings('customer-services/activity'));
  static customerServicesBilling: Signal<IDomainSettings> = signal(DomainSettings.getSettings('customer-services/billing'));
  static summary: Signal<IDomainSettings> = signal(DomainSettings.getSettings('summary'));
  static leadSummary: Signal<IDomainSettings> = signal(DomainSettings.getSettings('lead/summary'));
  static terminals: Signal<IDomainSettings> = signal(DomainSettings.getSettings('terminals'));
  static upsell: Signal<IDomainSettings> = signal(DomainSettings.getSettings('upsell'));
  static webSiteUrl: Signal<IDomainSettings> = signal(DomainSettings.getSettings('web-site/url'));
  static webSiteProducts: Signal<IDomainSettings> = signal(DomainSettings.getSettings('web-site/products'));
  static message: Signal<IDomainSettings> = signal(DomainSettings.getSettings('message'));
  static confirm: Signal<IDomainSettings> = signal(DomainSettings.getSettings('message/confirm'));
  static personaGuide: Signal<IDomainSettings> = signal(DomainSettings.getSettings('message/guide/persona-guide'));
  static clientsPrivacy: Signal<IDomainSettings> = signal(DomainSettings.getSettings('clients-privacy'));
  static disclosure: Signal<IDomainSettings> = signal(DomainSettings.getSettings('disclosure'));

  /**
   * private interface to access | manipulate settings map
   **/
  private static getSettings(domain: TAppDomain): IDomainSettings {
    return DomainSettings._settingsMap.get(domain) ?? DEFAULT_DOMAIN_SETTINGS;
  }

  private static createSettings(specificSettings?: Partial<IDomainSettings>): IDomainSettings {
    return { ...DEFAULT_DOMAIN_SETTINGS, ...specificSettings } as IDomainSettings;
  }
}
